import MaterialCommunityIcons from "@expo/vector-icons/MaterialCommunityIcons";
import {SafeAreaView, StyleSheet, Text, TouchableOpacity, View, Pressable, ScrollView} from "react-native";
import React, {useState, useEffect} from "react";
import CampTypes from "../../../../data/CampTypes";
import FilterModalRangeFilter from './FilterModalRangeFilter';
import FilterModalCategories from './FilterModalCategories';
import FilterModalPillSelector from './FilterModalPillSelector';


const SessionFilterModal = (
    {
        sessions,
        cachedSessions,
        setSessions,
        handleModalClose,
        selectedSessionTypes,
        selectedCategories,
        setSelectedCategories,
        setSelectedSessionTypes,
        cachedCamps,
        showAvailableOnly,
        setShowAvailableOnly,
        classSizeValues,
        setClassSizeValues,
        sessionPriceValues,
        setSessionPriceValues,
        sessionAgeValues,
        setSessionAgeValues,
    }
) => {
    const [filteredSessions, setFilteredSessions] = useState(cachedSessions || sessions || []);

    const sessionTypes = ['DAY', 'HALF-DAY', 'EXTENDED', 'CLINIC', 'OVERNIGHT'];

    const clearFilters = () => {
        setSelectedSessionTypes([]);
        setShowAvailableOnly(false);
        setClassSizeValues([1, 100]);
        setSessionPriceValues([0, 2000]);
        setSessionAgeValues([0, 30]);
        setSelectedCategories([]);
        setFilteredSessions(cachedSessions || []);
        setSessions(cachedSessions || []);
    }

    const filterSessions = (
        {
            sessionTypes=selectedSessionTypes,
            availableOnly=showAvailableOnly,
            classSize=classSizeValues,
            sessionPrice=sessionPriceValues,
            sessionAge=sessionAgeValues,
        }
    ) => {
        // Always start from cachedSessions (full baseline), not the currently filtered sessions
        let filtered = [...(cachedSessions || [])];

        // Apply camp categories selection to sessions via associated camp
        if (selectedCategories && selectedCategories.length > 0 && Array.isArray(cachedCamps)) {
            const selectedCategoryIconNames = selectedCategories
                .map(text => CampTypes.find(type => type.text === text)?.iconName)
                .filter(Boolean);
            filtered = filtered.filter(session => {
                const associatedCamp = cachedCamps.find(camp => camp.campId === session.campId);
                if (!associatedCamp) return false;
                return selectedCategoryIconNames.includes(associatedCamp.campCategory);
            });
        }

        if (sessionTypes.length > 0) {
            filtered = filtered.filter(session => sessionTypes.includes(session.sessionType));
        }

        if (availableOnly) {
            filtered = filtered.filter(session => session.registrationStatus !== 'FULL');
        }

        filtered = filtered.filter(session => session.capacity  >= classSize[0] && session.capacity <= classSize[1]);

        filtered = filtered.filter(session => session.price >= sessionPrice[0] && session.price <= sessionPrice[1]);

        filtered = filtered.filter(session => session.startAge >= sessionAge[0] && session.endAge <= sessionAge[1]);

        setFilteredSessions(filtered);
    };

    const toggleSessionTypes = (st) => {
        setSelectedSessionTypes((prevSessionTypes) => {
            const newSessionTypes = prevSessionTypes.includes(st)
                ? prevSessionTypes.filter(item => item !== st)
                : [...prevSessionTypes, st];
            return newSessionTypes;
        });
    };

    const toggleAvailableOnly = () => {
        setShowAvailableOnly(!showAvailableOnly);
    };

    const onFilter = () => {
        setSessions(filteredSessions);
        handleModalClose();
    }

     useEffect(() => {
         filterSessions({});
     }, [selectedSessionTypes, showAvailableOnly, classSizeValues, sessionPriceValues, sessionAgeValues, cachedSessions, selectedCategories, cachedCamps]);

    return (
        <SafeAreaView style={{flex: 1}}>
            <View style={{flexDirection: 'row', alignItems: 'center', justifyContent: 'center', marginBottom: 5}}>
                <TouchableOpacity onPress={handleModalClose} style={{position: 'absolute', left: 0}}>
                    <MaterialCommunityIcons name="close" size={20} color="black"/>
                </TouchableOpacity>

                <Text style={{fontSize: 18, fontWeight: '600'}}>Session Filters</Text>
            </View>
            <View style={styles.divider}/>

            <ScrollView style={styles.container}>
                <FilterModalCategories
                    selectedCategories={selectedCategories || []}
                    onChange={(newCategories) => setSelectedCategories(newCategories)}
                />
                <FilterModalPillSelector
                    title={'Session Types'}
                    items={sessionTypes}
                    selectedItems={selectedSessionTypes}
                    onToggle={toggleSessionTypes}
                    columns={3}
                    includeAllPill
                    allLabel="All"
                    allIconName="all-inclusive"
                    onSelectAll={() => setSelectedSessionTypes([])}
                    iconResolver={(item) => {
                        switch (item) {
                            case 'DAY': return 'white-balance-sunny';
                            case 'HALF-DAY': return 'weather-partly-cloudy';
                            case 'EXTENDED': return 'clock-outline';
                            case 'CLINIC': return 'stethoscope';
                            case 'OVERNIGHT': return 'weather-night';
                            default: return undefined;
                        }
                    }}
                />

                <FilterModalRangeFilter
                    fieldName={'Price'}
                    fieldValue={sessionPriceValues}
                    fieldValueSetter={setSessionPriceValues}
                    min={0}
                    max={2000}
                    unitName="$"
                />

                <FilterModalRangeFilter
                    fieldName={'Class Size'}
                    fieldValue={classSizeValues}
                    fieldValueSetter={setClassSizeValues}
                    min={1}
                    max={100}
                    unitName=" people"
                />

                <FilterModalRangeFilter
                    fieldName={'Age'}
                    fieldValue={sessionAgeValues}
                    fieldValueSetter={setSessionAgeValues}
                    min={0}
                    max={30}
                    unitName=" years"
                />
            </ScrollView>

            <View style={[styles.divider, {marginTop: 50}]}/>
            <View style={{flexDirection: 'row', justifyContent: 'center', alignItems: 'center', marginTop: 10}}>
                <Pressable onPress={clearFilters} style={{position: 'absolute', left: 0}}>
                    <Text style={{fontSize: 18, fontWeight: '600'}}>Clear All</Text>
                </Pressable>

                <Pressable style={styles.showButton} onPress={onFilter}>
                    <Text style={styles.logoutButtonText}>Show {filteredSessions.length} sessions</Text>
                </Pressable>
            </View>

        </SafeAreaView>
    )
};

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#ffffff',
        paddingLeft: 10,
        paddingTop: 10,
        paddingRight: 30,
        marginBottom: 10,
    },
    showButton: {
        backgroundColor: 'black',
        paddingVertical: 10,
        borderRadius: 5,
        width: '35%',
        alignItems: 'center',
        justifyContent: 'center',
    },
    logoutButtonText: {
        color: '#ffffff',
        fontSize: 15,
        fontWeight: 'bold',
    },
    divider: {
        height: 2,
        backgroundColor: 'lightgray',
        marginBottom: 5,
        marginTop: 10,
    },
    compactToggleContainer: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'flex-start',
        marginTop: 25,
        marginBottom: 35,
    },
    compactToggleLabel: {
        fontSize: 18,
        fontWeight: 'bold',
        marginRight: 15,
    },
    coolToggle: {
        width: 50,
        height: 28,
        borderRadius: 14,
        backgroundColor: '#e0e0e0',
        padding: 2,
        justifyContent: 'center',
    },
    coolToggleActive: {
        backgroundColor: '#4CAF50',
    },
    toggleThumb: {
        width: 24,
        height: 24,
        borderRadius: 12,
        backgroundColor: 'white',
        shadowColor: '#000',
        shadowOffset: { width: 0, height: 2 },
        shadowOpacity: 0.2,
        shadowRadius: 2,
        elevation: 3,
    },
    toggleThumbActive: {
        transform: [{ translateX: 22 }],
    },
});

export default SessionFilterModal;